// src/components/Navbar/Navbar.jsx
import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Tooltip,
  alpha,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Stack,
} from "@mui/material";
import {
  Logout,
  Menu as MenuIcon,
  KeyboardArrowDown,
  Person,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const Navbar = ({ onMenuClick }) => {
  const navigate = useNavigate();

  /* ---------------- STATE (UNCHANGED) ---------------- */
  const [students, setStudents] = useState([]);
  const [activeStudent, setActiveStudent] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  /* ---------------- INIT (UNCHANGED) ---------------- */
  useEffect(() => {
    const storedStudents = JSON.parse(localStorage.getItem("students")) || [];
    const storedActive = JSON.parse(localStorage.getItem("active_student"));

    setStudents(storedStudents);

    if (storedActive) {
      setActiveStudent(storedActive);
    } else if (storedStudents.length > 0) {
      setActiveStudent(storedStudents[0]);
      localStorage.setItem("active_student", JSON.stringify(storedStudents[0]));
    }
  }, []);

  /* ---------------- HANDLERS (UNCHANGED) ---------------- */
  const handleProfileClick = (e) => {
    if (students.length > 1) {
      setAnchorEl(e.currentTarget);
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleSwitchStudent = (student) => {
    setActiveStudent(student);
    localStorage.setItem("active_student", JSON.stringify(student));
    handleClose();
    window.location.reload();
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  if (!activeStudent) return null;

  /* ---------------- UI ---------------- */
  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: "transparent",
        boxShadow: "none",
        zIndex: (t) => t.zIndex.drawer - 1,
      }}
    >
      <Toolbar disableGutters sx={{ minHeight: { xs: 64, sm: 72 }, px: 0 }}>
        {/* GLASS CONTAINER */}
        <Box
          sx={{
            width: "100%",
            mx: 1.5,
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1, sm: 1.25 },
            borderRadius: 3,
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(15,23,42,0.92))",
            border: "1px solid rgba(148,163,184,0.35)",
            boxShadow:
              "0 18px 40px rgba(15,23,42,0.65), 0 0 0 1px rgba(15,23,42,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* LEFT */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={onMenuClick}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                borderRadius: 2,
                bgcolor: "rgba(15,23,42,0.9)",
                border: "1px solid rgba(148,163,184,0.5)",
                "&:hover": { bgcolor: "rgba(15,23,42,1)" },
              }}
            >
              <MenuIcon sx={{ color: "#E5E7EB", fontSize: 22 }} />
            </IconButton>

            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: 18, sm: 20 },
                  background:
                    "linear-gradient(90deg, #ffffff 0%, #cbd5f5 40%, #818cf8 100%)",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Welcome Back 👋
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  color: "rgba(148,163,184,0.85)",
                }}
              >
                Track your child’s activities & progress
              </Typography>
            </Box>
          </Stack>

          {/* RIGHT */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {/* STUDENT SWITCHER */}
            <Box
              onClick={handleProfileClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.2,
                py: 0.4,
                borderRadius: 999,
                bgcolor: "rgba(15,23,42,0.85)",
                border: "1px solid rgba(55,65,81,0.9)",
                cursor: students.length > 1 ? "pointer" : "default",
              }}
            >
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: "#4F46E5",
                  fontSize: 14,
                }}
              >
                {activeStudent.name?.[0] || <Person />}
              </Avatar>

              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#E5E7EB",
                  }}
                >
                  {activeStudent.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "rgba(148,163,184,0.9)",
                  }}
                >
                  Student
                </Typography>
              </Box>

              {students.length > 1 && (
                <KeyboardArrowDown sx={{ color: "#94A3B8", fontSize: 18 }} />
              )}
            </Box>

            {/* LOGOUT */}
            <Tooltip title="Logout">
              <IconButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  bgcolor: "rgba(15,23,42,0.85)",
                  border: "1px solid rgba(55,65,81,0.9)",
                }}
              >
                <Logout sx={{ color: "#EF4444", fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Toolbar>

      {/* DROPDOWN (UNCHANGED LOGIC) */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            background: "rgba(15,23,42,0.98)",
            border: "1px solid rgba(55,65,81,0.5)",
            color: "#E5E7EB",
            borderRadius: "16px",
            minWidth: 220,
          },
        }}
      >
        {students.map((stu) => (
          <MenuItem
            key={stu.guid}
            selected={stu.guid === activeStudent.guid}
            onClick={() => handleSwitchStudent(stu)}
            sx={{ borderRadius: "12px", mx: 1 }}
          >
            <Avatar sx={{ mr: 1 }}>{stu.name[0]}</Avatar>
            <ListItemText primary={stu.name} />
          </MenuItem>
        ))}
      </Menu>
    </AppBar>
  );
};

export default Navbar;
